import {FC, useEffect, useState, ChangeEvent} from 'react';
import {toast} from 'react-toastify';
//mui
import {
  Box,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
//store
import {getCurrentEventSelector} from '@/store/organization-event';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getTablesAndTicketsTiersSelector} from '@/store/organization-tables';
import {
  getAttendee,
  getAttendeeSelector,
  getAttendeesList,
  getAttendeesListSelector,
  getAttendeesStatistics,
  getAttendeeStatisticsSelector,
} from '@/store/organization-attendees';
//components
import Loader from '@/shared/components/Loader';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Checkbox from '@/shared/components/Checkbox';
import Modal from '@/shared/components/Modal';
import AttendeeDetails from '../AttendeeDetails';
//types
import {IFilterGroup, IOrganizationAttendee} from '@/utils/types';
//helpers
import {camelize, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
import {
  getUpdatedFilterData,
  handleSetTierIds,
  attendeesDefaultFilterValues,
  tableAttendeesMenuContent,
  updateAttendeesFilterOptions,
  attendeesDefaultFilter,
  attendeesDefaultFilterValuesMobile,
} from './helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
import {ReactComponent as RefreshIcon} from '@/assets/icons/refresh.svg';
import {ReactComponent as TickIcon} from '@/assets/icons/tick.svg';

//services
import {OrganizationAttendeesService} from '@/utils/services';
//constants
import {
  BODY1,
  HASH,
  RIGHT,
  STATUS,
  TABLE,
  TYPE_OF_SEAT,
  TABLE_PURCHASER,
  TICKETS_TIERS,
  defaultPage,
  defaultPageSize,
  ALL_TICKETS_TIERS,
  BID,
  VIP,
  VIP_STATUS,
  SPECIAL_MEAL,
  UPDATE_FROM_CONTACT_LIST,
  CHECKED_IN_DASH,
  CENTER,
  BIDDER_ID,
  NAME,
  CHECKED_IN,
  PRICE,
} from '@/utils/constants';
//enums
import {
  ATTENDEE_STATUS,
  MENU_OPTION,
  TABLE_SPECIAL_MEAL,
  TABLE_VIP_STATUS,
} from '@/utils/enums/tables.enum';
//hooks
import {usePaymentStatusPolling, useSkipFirstRenderEffect} from '@/utils/hooks';
//styles
import {attendeesTableStyle} from './AttendeesTable.styles';
import {UpdateAttendeesFromContactList} from '../UpdateAttendeesFromContactList';
import {useNavigate} from 'react-router-dom';
import {ROUTER_URL, TABLES_ENTITIES} from '@/utils/enums';
import {filterMenuStyle} from '@/shared/components/Table/FilterMenu/FilterMenu.styles';
import FilterContent from '@/shared/components/Table/FilterMenu/FilterContent';
import TableDetails from '../../TablesAndTickets/TableDetails';
import {tablesAndTicketsTableStyle} from '../../TablesAndTickets/TablesAndTicketsTable/TablesAndTicketsTable.styles';
import {attendeeDetailsTabs} from '../AttendeeDetails/helpers';
import {currencyFormatter} from '@/utils/helpers/currency';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {orgAPI} from '@/core/api';

const AttendeesTable: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isMobile = useMediaQuery('(max-width:767.98px)');
  const isLaptop = useMediaQuery('(min-width:1200px)');
  const isHover = true;

  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(true);
  const [page, setPage] = useState<number>(defaultPage);
  const [searchName, setSearchName] = useState<string>('');

  const [filterData, setFilterData] = useState<IFilterGroup[]>(
    isMobile ? attendeesDefaultFilterValuesMobile : attendeesDefaultFilterValues
  );
  const [filterValues, setFilterValues] = useState(attendeesDefaultFilter);

  const [activeAttendeeTab, setActiveAttendeeTab] = useState<string>('');
  const [isShowTicketDetailsModal, setIsShowTicketDetailsModal] = useState<boolean>(false);
  const [isShowUpdateContactListModal, setIsShowUpdateContactListModal] = useState<boolean>(false);
  const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);
  const [detailsTicketId, setDetailsTicketId] = useState<number>();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const attendeesStatistics = useAppSelector(getAttendeeStatisticsSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const attendeesList = useAppSelector(getAttendeesListSelector);
  const ticketsTiers = useAppSelector(getTablesAndTicketsTiersSelector);
  const attendee = useAppSelector(getAttendeeSelector);

  const {data, pageSize, totalCount} = attendeesList;

  const {startPollingFromUrl} = usePaymentStatusPolling({
    onCompleted: () => {
      getAttendeesListData();
    },
    apiInstance: orgAPI,
  });

  useSkipFirstRenderEffect(() => {
    if (currentEvent) getAttendeesListData(searchName);
  }, [page, filterValues]);

  useEffect(() => {
    if (ticketsTiers) setFilterData(updateAttendeesFilterOptions(ticketsTiers, filterData));
  }, [ticketsTiers]);

  const handleOpenPopup = () => setIsPopupOpen(!isPopupOpen);

  const handleUpdateContactListModalChange = () => {
    setIsShowUpdateContactListModal(!isShowUpdateContactListModal);
    if (isShowUpdateContactListModal) getAttendeesListData();
  };

  const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
    setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

    if (!isShowAttendeeDetailsModal && attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    } else getAttendeesListData();
  };

  const getAttendeesListData = async (value?: string) => {
    setIsDataLoaded(false);

    try {
      const reqBody = {
        eventId: currentEvent!.id,
        page: isLaptop ? page : 1,
        pageSize: isLaptop ? defaultPageSize : 10000,
        searchName: value,
        ...filterValues,
      };

      await dispatch(getAttendeesList(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const handleSortChange = (newSortColumn: string) => () => {
    const isAsc = filterValues.sortColumn !== newSortColumn || !filterValues.isOrderDesc;

    setFilterValues((prevState) => ({
      ...prevState,
      isOrderDesc: isAsc,
      sortColumn: newSortColumn,
    }));
  };

  const handleSearchChange = (value: string) => {
    setSearchName(value);
    getAttendeesListData(value);
  };

  const handlePressEnter = () => {
    setPage(defaultPage);
    if (currentEvent) getAttendeesListData(searchName);
  };

  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

  const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus, isHover);

  const updateFilterData = (groupTitle: string, optionTitle: string, id?: number) => {
    if (optionTitle === ALL_TICKETS_TIERS && ticketsTiers) {
      return setFilterData(updateAttendeesFilterOptions(ticketsTiers, filterData));
    }

    return setFilterData((prevFilterData) => getUpdatedFilterData(prevFilterData, groupTitle, optionTitle, id));
  };

  const handleFilterChange = (groupTitle: string, optionTitle: string, id?: number) => {
    updateFilterData(groupTitle, optionTitle, id);

    switch (groupTitle) {
      case STATUS:
        if (optionTitle === ATTENDEE_STATUS.ALL_ATTENDEES) {
          setFilterValues((prevState) => {
            const {attendeeStatus, ...rest} = prevState;
            return rest;
          });
        } else if (optionTitle === ATTENDEE_STATUS.NOT_CHECKED_IN) {
          setFilterValues((prevState) => ({
            ...prevState,
            attendeeStatus: ATTENDEE_STATUS.PENDING,
          }));
        } else {
          setFilterValues((prevState) => ({
            ...prevState,
            attendeeStatus: camelize(optionTitle),
          }));
        }
        break;

      case VIP_STATUS:
        setFilterValues((prevState) => ({
          ...prevState,
          isOnlyVip: optionTitle === TABLE_VIP_STATUS.ONLY_VIP,
        }));
        break;

      case SPECIAL_MEAL:
        setFilterValues((prevState) => ({
          ...prevState,
          isSpecialMeal: optionTitle === TABLE_SPECIAL_MEAL.ONLY_SPECIAL_MEAL,
        }));
        break;

      case TICKETS_TIERS:
        const newTicketTiersId = handleSetTierIds(filterValues.ticketTiersId, id);
        setFilterValues({
          ...filterValues,
          ticketTiersId: newTicketTiersId as null,
        });
        break;

      default:
        break;
    }
  };

  const handleDeleteAttendee = async (attendeeData: IOrganizationAttendee) => {
    if (!currentEvent || attendeeData.status === ATTENDEE_STATUS.CHECKED_IN || attendeeData.isUnknown) return;
    try {
      await OrganizationAttendeesService.deleteAttendeeData(attendeeData.id);
      await getAttendeesListData();
      await dispatch(getAttendeesStatistics(currentEvent.id)).unwrap();
      toast.success('Attendee has been converted to an unknown guest');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateAttendeeVip = (_: ChangeEvent<HTMLInputElement>, isVip: boolean, attendeeId: number) => {
    const updateAttendeeVip = async () => {
      try {
        await OrganizationAttendeesService.updateAttendeesVip(attendeeId, isVip);
        toast.success('Attendee VIP status has been updated');
        getAttendeesListData();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    updateAttendeeVip();
  };

  const handleUpdateAttendeeStatus = (attendee: IOrganizationAttendee) => {
    if (!currentEvent || attendee.isUnknown) return;

    const currentAttendeeStatus = data.find((item) => item.id === attendee.id)?.status;
    const newStatus =
      currentAttendeeStatus === ATTENDEE_STATUS.CHECKED_IN ? ATTENDEE_STATUS.PENDING : ATTENDEE_STATUS.CHECKED_IN;

    const updateAttendeeStatus = async () => {
      try {
        await OrganizationAttendeesService.updateAttendeesStatus(attendee.id, newStatus);
        await getAttendeesListData();
        await dispatch(getAttendeesStatistics(currentEvent.id)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    updateAttendeeStatus();
  };

  const handleOpenTicketDetailsModal = (tableId?: number) => {
    setIsShowTicketDetailsModal(!isShowTicketDetailsModal);
    if (!isShowTicketDetailsModal && tableId) setDetailsTicketId(tableId!);
  };

  const sendPaymentLink = async (attendee: IOrganizationAttendee) => {
    if (!currentEvent || !attendee) return;

    if (attendee.paidStatus === null) {
      toast.info('Attendee hasn`t any contributions yet');
      return;
    }

    try {
      await OrganizationStripeService.sendPaymentLink(attendee.id, currentEvent?.id);
      toast.success('Payment link sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleClickMenuOption = (option: string, attendee: IOrganizationAttendee, tableId?: number) => async () => {
    switch (option) {
      case MENU_OPTION.PATRON_DETAILS:
        setActiveAttendeeTab(attendeeDetailsTabs[0]);

        isLaptop
          ? handleChangeAttendeeDetailsModal(attendee.id)
          : navigate(`${ROUTER_URL.ORGANIZATION_ATTENDEES}/${attendee.id}`);
        break;

      case MENU_OPTION.CANCEL_CHECK_IN:
        handleUpdateAttendeeStatus(attendee);
        break;

      case MENU_OPTION.CHECK_IN:
        handleUpdateAttendeeStatus(attendee);
        break;

      case MENU_OPTION.DELETE:
        await handleDeleteAttendee(attendee);
        break;

      case MENU_OPTION.TABLE_DETAILS:
        isLaptop
          ? handleOpenTicketDetailsModal(tableId)
          : navigate(`${ROUTER_URL.ORGANIZATION_TABLES_DETAILS}/${tableId}`);
        break;

      case MENU_OPTION.COLLECT_PAYMENT:
        setActiveAttendeeTab(attendeeDetailsTabs[2]);
        isLaptop
          ? handleChangeAttendeeDetailsModal(attendee.id)
          : navigate(`${ROUTER_URL.ORGANIZATION_ATTENDEES}/${attendee.id}`);
        break;

      case MENU_OPTION.SEND_PAYMENT_LINK:
        sendPaymentLink(attendee);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchName, filterValues]);

  useEffect(() => {
    startPollingFromUrl();
  }, []);

  return (
    <Box sx={attendeesTableStyle.wrapper}>
      <SearchHeader
        page={page}
        items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
        total={totalCount}
        searchPlaceholder="Search Attendees"
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        filterOption={filterData}
        parentClassName={{padding: '0 16px'}}
        isMobileMode={isMobile}
        onOpenDropdown={handleOpenPopup}
        unitName={TABLES_ENTITIES.GUESTS}
        actionButton={
          <Button
            onClick={handleUpdateContactListModalChange}
            title={UPDATE_FROM_CONTACT_LIST}
            startIcon={<RefreshIcon />}
            sx={attendeesTableStyle.searchButton}
            disabled={!attendeesList.data?.length}
          />
        }
      />

      {isDataLoaded ? (
        totalCount ? (
          <Box sx={attendeesTableStyle.tableContent}>
            <TableContainer sx={attendeesTableStyle.container}>
              <Table sx={attendeesTableStyle.table}>
                <TableHead>
                  <TableRow sx={attendeesTableStyle.tableHeader}>
                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(BID)} IconComponent={UpDownIcon}>
                        {BIDDER_ID}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{NAME}</TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(TABLE)} IconComponent={UpDownIcon}>
                        {TABLE}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{PRICE}</TableCell>

                    <TableCell>{TYPE_OF_SEAT}</TableCell>

                    <TableCell>{TABLE_PURCHASER}</TableCell>

                    <TableCell>{CHECKED_IN} ?</TableCell>

                    <TableCell align={isMobile ? RIGHT : CENTER}>{VIP}</TableCell>

                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data &&
                    data.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={attendeesTableStyle.tableBody}
                        onClick={() => !isLaptop && handleClickMenuOption(MENU_OPTION.PATRON_DETAILS, row)()}
                      >
                        <TableCell>
                          {HASH}
                          {row.id}
                        </TableCell>

                        <TableCell
                          onClick={handleClickMenuOption(MENU_OPTION.PATRON_DETAILS, row)}
                          style={{cursor: 'pointer'}}
                        >
                          {row.userInfo?.firstName && row.userInfo?.lastName
                            ? `${row.userInfo?.firstName}  ${row.userInfo?.lastName}`
                            : `Guest of ${row.table.purchaser?.firstName} ${row.table.purchaser?.lastName}`}
                          <Typography>{isMobile && `${HASH} ${row.table.tickets[0].id}`}</Typography>
                        </TableCell>

                        <TableCell>{row.table.number}</TableCell>
                        <TableCell>{currencyFormatter.format(row.table.tableInfo.price)}</TableCell>

                        <TableCell>
                          <Box>
                            <Typography>{row.table.name}</Typography>
                            <Typography>{row.table.tableInfo.tier}</Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box>
                            <Typography>
                              {row.table.purchaser?.firstName} {row.table.purchaser?.lastName}
                            </Typography>
                            <Typography
                              sx={{
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                              }}
                            >
                              {row.table.purchaser?.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            onClick={handleClickMenuOption(MENU_OPTION.PATRON_DETAILS, row)}
                            sx={{
                              ...attendeesTableStyle.status,
                              ...getPayStatusSx(row.status),
                            }}
                          >
                            {row.status === ATTENDEE_STATUS.PENDING
                              ? 'No'
                              : ATTENDEE_STATUS.CHECKED_IN.replace(/\bCheckedIn\b/g, CHECKED_IN_DASH)}
                            {row.status === ATTENDEE_STATUS.PENDING ? (
                              <Box sx={attendeesTableStyle.pendingStatus}>
                                <EllipseIcon />
                              </Box>
                            ) : (
                              <TickIcon
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  stroke: 'var(--success-600)',
                                }}
                              />
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            value={row.isVIP}
                            onChange={(event, checked) => handleUpdateAttendeeVip(event, checked, row.id)}
                            isStarIcon
                            disableRipple
                          />
                        </TableCell>
                        <TableCell>
                          <ActionsMenu
                            disableRipple
                            content={
                              <Box>
                                {tableAttendeesMenuContent.map((item) => {
                                  const menuAction =
                                    item === MENU_OPTION.CHECK_IN && row.status === ATTENDEE_STATUS.CHECKED_IN
                                      ? MENU_OPTION.CANCEL_CHECK_IN
                                      : item;

                                  return (
                                    <Typography
                                      key={item}
                                      onClick={handleClickMenuOption(menuAction, row, row.table.id)}
                                      variant={BODY1}
                                      sx={{
                                        ...attendeesTableStyle.menuItem,
                                        ...(item === MENU_OPTION.DELETE &&
                                          (row.status === ATTENDEE_STATUS.CHECKED_IN || row.isUnknown) &&
                                          attendeesTableStyle.hideMenuItem),
                                        ...(item === MENU_OPTION.CHECK_IN &&
                                          row.status === ATTENDEE_STATUS.PENDING &&
                                          row.isUnknown &&
                                          attendeesTableStyle.hideMenuItem),
                                        ...(item === MENU_OPTION.COLLECT_PAYMENT &&
                                          row.isUnknown &&
                                          attendeesTableStyle.hideMenuItem),
                                        ...(item === MENU_OPTION.SEND_PAYMENT_LINK &&
                                          row.isUnknown &&
                                          attendeesTableStyle.hideMenuItem),
                                      }}
                                    >
                                      {menuAction}
                                    </Typography>
                                  );
                                })}
                              </Box>
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={attendeesTableStyle.pagination}>
              <Pagination page={page} pageSize={pageSize} total={totalCount} onChange={(step) => setPage(step)} />
            </Box>
          </Box>
        ) : (
          <EmptyTableData text={attendeesStatistics?.allGuests ? 'No Data Found' : 'You do not have any attendees'} />
        )
      ) : (
        <Loader parentClassName={attendeesTableStyle.loader} size={50} />
      )}
      <Drawer anchor="bottom" open={isPopupOpen} onClose={handleOpenPopup}>
        <Box>
          <Typography sx={{p: 2, fontWeight: 600, fontSize: 16}}>Filters</Typography>
          <Box sx={filterMenuStyle.wrapper}>
            <FilterContent options={filterData} onFilterChange={handleFilterChange} />
          </Box>
        </Box>
      </Drawer>
      <Modal
        onClose={handleUpdateContactListModalChange}
        open={isShowUpdateContactListModal}
        sx={attendeesTableStyle.modal}
        title={UPDATE_FROM_CONTACT_LIST}
      >
        <UpdateAttendeesFromContactList onCloseModal={handleUpdateContactListModalChange} />
      </Modal>

      <Modal
        onClose={handleChangeAttendeeDetailsModal}
        open={isShowAttendeeDetailsModal && !!attendee}
        sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
      >
        <AttendeeDetails
          onCloseModal={handleChangeAttendeeDetailsModal}
          handleProfileTabStyles={handleProfileTabStyles}
          activeAttendeeTab={activeAttendeeTab}
        />
      </Modal>
      <Modal
        onClose={handleOpenTicketDetailsModal}
        open={isShowTicketDetailsModal}
        sx={tablesAndTicketsTableStyle.modal}
      >
        <TableDetails onCloseModal={handleOpenTicketDetailsModal} currentTableId={detailsTicketId} />
      </Modal>
    </Box>
  );
};

export default AttendeesTable;
